/* eslint-disable react/no-array-index-key */
import React, { useState, useRef, useEffect } from 'react';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import IcomoonReact from 'icomoon-react';
import { useDispatch, useSelector } from 'react-redux';

import CollapsedSubCalendar from './CollapsedSubCalendar';
import iconSet from '../../../../shared/images/teambuildr-selection.json';
import { setSelectedReduxCalendar } from '../../ducks/calendarActions';

const CollapsibleCalendarDiv = styled('div')`
  cursor: pointer;
  margin-bottom: 10px;
  font-family: 'Nunito Sans';
  font-size: 18px;
  font-weight: 600;
  color: #444444;
  background-color: ${({ isSelected }) => (isSelected ? '#f0f0f0' : 'transparent')};
`;

const SubCalendarsContainer = styled('div')`
  height: ${({ isOpen, contentHeight }) => (isOpen ? `${contentHeight}px` : '0')};
  overflow: hidden;
  transition: height 0.3s ease-in-out;
  padding-left: 65px;
  margin-top: 10px;
`;

const IconAndName = styled('div')`
  display: flex;
  align-items: center;
`;

const IconContainer = styled('div')`
  height: 100%;
  width: 30px;
  display: flex;
  justify-content: center;
  align-items: center;
  transition: transform 0.3s ease-in-out;
  transform: ${({ isOpen }) => (isOpen ? 'rotate(180deg)' : 'rotate(0deg)')};
`;

const CollapsibleCalendar = ({ calendar, parentCalendarIndex, isSelected }) => {
  const [isOpen, setIsOpen] = useState(true);
  const [contentHeight, setContentHeight] = useState(0);
  const contentRef = useRef(null);
  const selectedReduxCalendar = useSelector((state) => state.calendar.data.selectedReduxCalendar);

  // Check if any child calendar is selected
  const hasSelectedChild = calendar.childCalendars.some(
    (child) => child.id === selectedReduxCalendar?.id,
  );

  const dispatch = useDispatch();

  useEffect(() => {
    if (contentRef.current) {
      setContentHeight(contentRef.current.scrollHeight);
    }
  }, [isOpen, calendar.childCalendars.length]);

  // Auto-expand if this calendar or any of its children is selected
  useEffect(() => {
    if ((isSelected || hasSelectedChild) && !isOpen) {
      setIsOpen(true);
    }
  }, [isSelected, hasSelectedChild, isOpen]);

  const toggleOpen = () => setIsOpen(!isOpen);

  const handleIconClick = (e) => {
    e.stopPropagation();
    toggleOpen();
  };

  return (
    <CollapsibleCalendarDiv
      onClick={() => {
        dispatch(setSelectedReduxCalendar(calendar));
      }}
      isSelected={isSelected}
      data-calendar-id={calendar.id}
      className={`parent-calendar-container ${isOpen ? 'expanded' : ''}`}
    >
      <IconAndName>
        <IconContainer
          onClick={handleIconClick}
          isOpen={isOpen}
          className='toggle-button'
        >
          <IcomoonReact
            iconSet={iconSet}
            size={12}
            icon='down-arrow'
            color='#E0E0E0'
          />
        </IconContainer>
        <div>{calendar.name}</div>
      </IconAndName>
      <SubCalendarsContainer isOpen={isOpen} contentHeight={contentHeight}>
        <div ref={contentRef}>
          {calendar.childCalendars
            .filter((subCalendar) => !subCalendar.archived)
            .map((subCalendar, index) => (
              <CollapsedSubCalendar
                key={index}
                subCalendar={subCalendar}
                parentCalendarIndex={parentCalendarIndex}
                isSelected={subCalendar.id === selectedReduxCalendar?.id}
              />
            ))}
        </div>
      </SubCalendarsContainer>
    </CollapsibleCalendarDiv>
  );
};

CollapsibleCalendar.propTypes = {
  calendar: PropTypes.instanceOf(Object).isRequired,
  parentCalendarIndex: PropTypes.number.isRequired,
  isSelected: PropTypes.bool,
};

CollapsibleCalendar.defaultProps = {
  isSelected: false,
};

export default CollapsibleCalendar;
